import axios, { AxiosRequestConfig } from 'axios';
import { Recipe, CreateRecipeDTO, UpdateRecipeDTO, RecipeListResponse } from '../types/recipe';

const getDefaultApiBaseUrl = (): string => {
  if (process.env.NODE_ENV === 'production') {
    return '';
  }

  if (typeof window !== 'undefined' && window.location?.hostname) {
    return `${window.location.protocol}//${window.location.hostname}:5001`;
  }

  return 'http://localhost:5001';
};

const DEFAULT_API_BASE_URL = getDefaultApiBaseUrl();

export type QueryValue = string | number | boolean | null | undefined;
export type QueryParams = Record<string, QueryValue>;

export const getApiBaseUrl = (): string => {
  return process.env.REACT_APP_API_URL || DEFAULT_API_BASE_URL;
};

export const encodeSegment = (value: string): string => {
  return encodeURIComponent(value);
};

export const compactParams = <T extends QueryParams>(params: T): Partial<T> => {
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== null)
  ) as Partial<T>;
};

export const buildUrl = (path: string, params?: QueryParams): string => {
  if (!params) {
    return path;
  }

  const query = new URLSearchParams(
    Object.entries(compactParams(params)).map(([key, value]) => [key, String(value)])
  ).toString();

  return query ? `${path}?${query}` : path;
};

// Create axios instance with default config
const api = axios.create({
  baseURL: getApiBaseUrl(),
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const url = error.config?.url;
    // Don't log 401s during auth check as they're expected when not logged in
    if (error.response?.status === 401 && url && !url.includes('current-user')) {
      console.error('Authentication error:', error);
      // Redirect to recipes page instead of home page when authentication fails
      if (window.location.pathname !== '/recipes' &&
          !window.location.pathname.startsWith('/login')) {
        console.log('Redirecting to recipes page due to auth error');
      }
    }
    return Promise.reject(error);
  }
);

export const getData = async <T>(url: string, config?: AxiosRequestConfig): Promise<T> => {
  if (config === undefined) {
    return (await api.get<T>(url)).data;
  }

  return (await api.get<T>(url, config)).data;
};

export const postData = async <T, B = unknown>(
  url: string,
  body?: B,
  config?: AxiosRequestConfig
): Promise<T> => {
  if (body === undefined && config === undefined) {
    return (await api.post<T>(url)).data;
  }

  if (config === undefined) {
    return (await api.post<T>(url, body)).data;
  }

  return (await api.post<T>(url, body, config)).data;
};

export const putData = async <T, B = unknown>(
  url: string,
  body?: B,
  config?: AxiosRequestConfig
): Promise<T> => {
  if (body === undefined && config === undefined) {
    return (await api.put<T>(url)).data;
  }

  if (config === undefined) {
    return (await api.put<T>(url, body)).data;
  }

  return (await api.put<T>(url, body, config)).data;
};

export const patchData = async <T, B = unknown>(
  url: string,
  body?: B,
  config?: AxiosRequestConfig
): Promise<T> => {
  if (body === undefined && config === undefined) {
    return (await api.patch<T>(url)).data;
  }

  if (config === undefined) {
    return (await api.patch<T>(url, body)).data;
  }

  return (await api.patch<T>(url, body, config)).data;
};

export const deleteData = async <T>(url: string, config?: AxiosRequestConfig): Promise<T> => {
  if (config === undefined) {
    return (await api.delete<T>(url)).data;
  }

  return (await api.delete<T>(url, config)).data;
};

export const deleteRequest = async (url: string, config?: AxiosRequestConfig): Promise<void> => {
  if (config === undefined) {
    await api.delete(url);
    return;
  }

  await api.delete(url, config);
};

// Recipe API endpoints
export const recipeApi = {
  create: (data: CreateRecipeDTO): Promise<Recipe> => postData<Recipe, CreateRecipeDTO>('/api/recipes', data),

  extractFromUrl: (url: string): Promise<CreateRecipeDTO> =>
    postData<CreateRecipeDTO, { url: string }>('/api/recipes/extract-url', { url }),

  list: (params?: {
    skip?: number;
    take?: number;
    search?: string;
  }): Promise<RecipeListResponse> => getData<RecipeListResponse>('/api/recipes', { params }),

  get: (id: string): Promise<Recipe> => getData<Recipe>(`/api/recipes/${id}`),

  update: (id: string, data: UpdateRecipeDTO): Promise<Recipe> =>
    putData<Recipe, UpdateRecipeDTO>(`/api/recipes/${id}`, data),

  delete: (id: string): Promise<void> => deleteRequest(`/api/recipes/${id}`),
};

export default api;
